import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { formatCurrency, formatDate, maskAddress, parseDateAndTime, shouldMaskDataReverse } from '../../helpers/UtilityHelper';
import { delivered, deliveryFailed, rto, transit, placed } from '../../common/util';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFMultipleSelectField, RHFSearchableSelectField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import { Link } from 'react-router-dom';
import _ from "lodash";
import { subDays } from "date-fns";
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FunctionsIcon from '@mui/icons-material/Functions';
import CancelIcon from '@mui/icons-material/Cancel';
import useNdr from "../../hooks/useNdr";
import Loader from '../../common/Loader';
import { recalculatePricing } from '../../common/recalculatePricing';
import { useSnackbar } from 'notistack';
import { ALL_SERVICE_TYPES, ALL_SERVICE_TYPES_OPTION, COURIER_LIST_QUERY_PAYLOAD, MerchantFilterCached } from '../../common/ProshipFormComponents';
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls';
import CancelOrder from './CancelOrder';
import MUIModal from '../../components/MuiModal';
import ResyncActionModal from './ResyncActionModal';
import PageTitle from '../../components/PageTitle';
import QcDetails from '../B2BOrder/QcDetails';
import { useSelector } from 'react-redux';
import { getToken } from '../../helpers/ReduxHelpers';
import jwt_decode from "jwt-decode";
import StatusChip from '../../common/StatusChip';
import OrderRowActions from '../../common/OrderRowActions';
import PaymentBadge from '../../common/PaymentBadge';
import ZoneBadge from '../../common/ZoneBadge';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';
import CopyIcon from '../../common/CopyIcon';

/**
 * Created Date defaults to the last 90 days and is always applied. Without a date bound (and with
 * the `_id` default sort) Mongo walked the orders `_id` index to find 10 reverse rows for the user's
 * accessible merchants: 95 s on prod for a 36-merchant PROSHIPADMIN. Bounding awbRegisteredDate plus
 * the explicit sort rides the merchant/orderStatus/orderType/shipmentType/awbRegisteredDate index.
 * Same remedy as B2BOrder/nonAbdFilter.js (NON_ABD_DEFAULT_WINDOW_DAYS).
 */
const RVP_DEFAULT_WINDOW_DAYS = 90;
// Fresh object each call so the dates are "now", not module-load time.
const defaultFilterValues = () => ({
    status: "", courier: null, merchant: '', reference: '', awb: '', city: '', state: '',
    fromDate: subDays(new Date(), RVP_DEFAULT_WINDOW_DAYS), toDate: new Date(),
    pickupPincode: '', zone: '', parent: '', serviceType: [], accountCode: [],
});

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);



const AwbLink = ({ awb, id }) => (
    <Box
        component={Link}
        to={`/order-detail/${awb}?id=${id}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            fontSize: '12px', fontWeight: 700, letterSpacing: '0.02em',
            color: '#1d4ed8', textDecoration: 'none',
            borderBottom: '1px dashed #93c5fd', pb: '1px',
            transition: 'color 0.15s, border-color 0.15s',
            '&:hover': { color: '#1e40af', borderBottomStyle: 'solid', borderBottomColor: '#1d4ed8' },
        }}
    >
        {awb}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{
        fontSize: '10px', fontWeight: 600, color: '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px',
    }}>
        {children}
    </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const AllRVPOrders = (props) => {
    const methods = useForm({
        defaultValues: defaultFilterValues()
    });

    // ── Parent + Service Type (+ optional Account Code) chained filter ────────
    //    (inline; this page does not use the shared FilterBar). Replaces the
    //    old single-step Courier filter.
    //      - All fields hide when AWB is filled
    //      - serviceType (MULTI) stays hidden until a parent is picked
    //      - accountCode (MULTI, OPTIONAL) stays hidden until parent + at
    //        least one service type are picked; empty = use all matching
    //      - Courier list scoped to RVP (only reverse couriers contribute)
    const watchedAwb = methods.watch('awb');
    const watchedParent = methods.watch('parent');
    const watchedServiceType = methods.watch('serviceType');
    // Same cache key as CourierFilter — no extra fetch.
    const { data: courierPartnersForFilter = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);
    // Scope to D2C (B2C) RVP (Reverse) couriers only — this is a D2C page, so B2B
    // couriers must not appear (matches type="RVP" + B2C shipment scope).
    const rvpCouriers = useMemo(
        () => (Array.isArray(courierPartnersForFilter) ? courierPartnersForFilter : []).filter((c) => c?.type === 'RVP' && (c?.supported_shipment === 'B2C' || c?.supportedShipmentType === 'B2C')),
        [courierPartnersForFilter]
    );
    const parentOptions = useMemo(
        () =>
            Array.from(new Set(rvpCouriers.map((c) => c?.parent).filter(Boolean)))
                .sort()
                .map((p) => ({ label: p, value: p })),
        [rvpCouriers]
    );
    const serviceTypeOptions = useMemo(() => {
        if (!watchedParent) return [];
        const types = new Set();
        rvpCouriers
            .filter((c) => c?.parent === watchedParent)
            .forEach((c) => {
                if (c?.serviceTypePrimary) types.add(c.serviceTypePrimary);
                if (c?.serviceTypeSecondary) types.add(c.serviceTypeSecondary);
            });
        const specific = Array.from(types).sort().map((t) => ({ label: t, value: t }));
        return [ALL_SERVICE_TYPES_OPTION, ...specific];
    }, [watchedParent, rvpCouriers]);

    // "Select All" toggle invariant for the Service Type multi-select. See the
    // matching block in src/app/common/FilterBar.js ServiceTypeFilterField for
    // the full rationale. Keeps the "All Service Types" sentinel and all the
    // specific values in sync so clicking "All" visually checks everything.
    const prevServiceTypeRef = useRef(watchedServiceType);
    useEffect(() => {
        const prev = Array.isArray(prevServiceTypeRef.current) ? prevServiceTypeRef.current : [];
        const curr = Array.isArray(watchedServiceType) ? watchedServiceType : [];
        const specificValues = serviceTypeOptions.filter((o) => o.value !== ALL_SERVICE_TYPES).map((o) => o.value);
        const prevHasAll = prev.includes(ALL_SERVICE_TYPES);
        const currHasAll = curr.includes(ALL_SERVICE_TYPES);

        if (!prevHasAll && currHasAll) {
            const next = [ALL_SERVICE_TYPES, ...specificValues];
            prevServiceTypeRef.current = next;
            methods.setValue('serviceType', next, { shouldDirty: true });
            return;
        }
        if (prevHasAll && !currHasAll) {
            prevServiceTypeRef.current = [];
            methods.setValue('serviceType', [], { shouldDirty: true });
            return;
        }
        if (prevHasAll && currHasAll && specificValues.length > 0) {
            const allChecked = specificValues.every((v) => curr.includes(v));
            if (!allChecked) {
                const next = curr.filter((v) => v !== ALL_SERVICE_TYPES);
                prevServiceTypeRef.current = next;
                methods.setValue('serviceType', next, { shouldDirty: true });
                return;
            }
        }
        prevServiceTypeRef.current = curr;
    }, [watchedServiceType, serviceTypeOptions, methods]);
    const accountCodeOptions = useMemo(() => {
        if (!watchedParent || !Array.isArray(watchedServiceType) || watchedServiceType.length === 0) return [];
        const includesAll = watchedServiceType.includes(ALL_SERVICE_TYPES);
        const matching = rvpCouriers.filter((c) => {
            if (c?.parent !== watchedParent) return false;
            if (includesAll) return true;
            const primaryHit = c?.serviceTypePrimary && watchedServiceType.includes(c.serviceTypePrimary);
            const secondaryHit = c?.serviceTypeSecondary && watchedServiceType.includes(c.serviceTypeSecondary);
            return primaryHit || secondaryHit;
        });
        const codes = new Set(matching.map((c) => c?.cpAccountCode).filter(Boolean));
        return Array.from(codes).sort().map((code) => ({ label: code, value: code }));
    }, [rvpCouriers, watchedParent, watchedServiceType]);
    const showParentFilter = !watchedAwb;
    const showServiceTypeFilter = !watchedAwb && !!watchedParent;
    const showAccountCodeFilter = !watchedAwb && !!watchedParent && Array.isArray(watchedServiceType) && watchedServiceType.length > 0;
    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    // Starts with the same defaults the form shows, so the first request already carries the
    // 90-day Created Date range the picker displays.
    const [filters, setFilters] = useState(() => defaultFilterValues());
    const [sizePerPage, setSizePerPage] = useState(10);
    const { enqueueSnackbar } = useSnackbar();
    const [reload, setReload] = useState(0);
    const [Cancell, setCancell] = useState(false);
    const [id, setId] = useState();
    const [qc, setQc] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [resyncModal, setResyncModal] = useState({ open: false, awb: null, reference: null });
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());

    const request = useMemo(() => {
        const includes = "id,is_reverse,reference,invoiceValue,pods,shippingLabelId,awbRegisteredDate,merchantPricing.zone,merchantDetail,courierDetail,pickupDetails.from_pincode,pickupDetails.from_city,deliveryDetails.to_pincode,deliveryDetails.to_state,deliveryDetails.to_city,channelName,orderStatus,shipmentDetail,paymentMode,awbNumber,referenceNumber,merchantPricing.price,qcDetails,orderType";
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "orderSubStatus", operator: "ne", value: "UNPROCESSED" });
        andfilter.push({ "field": "orderType", operator: "eq", value: "REVERSE SHIPMENT" });
        andfilter.push({ "field": "shipmentType", operator: "eq", value: 'B2C' });
        andfilter.push({ "field": "merchantId", operator: "ne", value: 260425082022 });

        if (filters.status) {
            andfilter.push({ "field": "orderStatus", "operator": "in", "value": [filters.status] });
        }
        if (filters.awb) {
            andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        }
        if (filters.city) {
            andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        }
        if (filters.state) {
            andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        }
        if (filters.merchant) {
            andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        }
        // Parent + Service Type (+ optional Account Code) — chained filter
        // that replaces the old single-step Courier filter. See AllOrders.js
        // for the same logic. RVP-only couriers are considered.
        const hasServiceType = Array.isArray(filters.serviceType) && filters.serviceType.length > 0;
        if (filters.parent && hasServiceType && Array.isArray(courierPartnersForFilter) && courierPartnersForFilter.length > 0) {
            // "All Service Types" sentinel — when present, skip the service-type
            // narrowing and consider every RVP courier under the parent.
            const includesAll = filters.serviceType.includes(ALL_SERVICE_TYPES);
            const candidateCouriers = courierPartnersForFilter.filter((c) => {
                if (c?.type !== 'RVP') return false;
                if (c?.parent !== filters.parent) return false;
                if (includesAll) return true;
                const primaryHit = c?.serviceTypePrimary && filters.serviceType.includes(c.serviceTypePrimary);
                const secondaryHit = c?.serviceTypeSecondary && filters.serviceType.includes(c.serviceTypeSecondary);
                return primaryHit || secondaryHit;
            });
            const hasAccountCode = Array.isArray(filters.accountCode) && filters.accountCode.length > 0;
            const finalCouriers = hasAccountCode
                ? candidateCouriers.filter((c) => filters.accountCode.includes(c?.cpAccountCode))
                : candidateCouriers;
            const matchedIds = finalCouriers.map((c) => 'ObjectId(\'' + c.id + '\')');
            if (matchedIds.length > 0) {
                andfilter.push({ field: 'courier.$id', operator: 'in', value: matchedIds });
            } else {
                andfilter.push({ field: 'courier.$id', operator: 'in', value: ['ObjectId(\'no-match\')'] });
            }
        }
        if (filters.reference) {
            orFilter.push({ "field": "reference", "operator": "eq", "value": filters.reference });
            orFilter.push({ "field": "shippingLabelId", "operator": "eq", "value": filters.reference });
        }
        // Created Date filters on awbRegisteredDate (a real date, indexed), not orderDate strings:
        // see RVP_DEFAULT_WINDOW_DAYS. Same field the merchant panel's Created Date filter uses.
        if (filters.fromDate) {
            andfilter.push({ field: "awbRegisteredDate", operator: "gte", value: `Date('${formatDate(filters.fromDate, 'yyyy-MM-dd')} 00:00:00')` });
        }
        if (filters.toDate) {
            andfilter.push({ field: "awbRegisteredDate", operator: "lte", value: `Date('${formatDate(filters.toDate, 'yyyy-MM-dd')} 23:59:59')` });
        }
        if (filters.pickupPincode) {
            andfilter.push({ field: "pickupDetails.from_pincode", operator: "eq", value: filters.pickupPincode });
        }
        if (filters.zone) {
            andfilter.push({ field: "merchantPricing.zone", operator: "eq", value: filters.zone });
        }
        const filter = {
            "andfilter": andfilter,
            "orfilter": orFilter,
            "offset": (page) * sizePerPage,
            "limit": sizePerPage,
            "skipCount": true,
            "includes": includes,
            "sort": { "field": "awbRegisteredDate", "direction": "DESC" }
        };
        return filter;
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage, reload]);

    let { data = [], isFetching, isLoading, refetch } = useNdr({ request: request });

    // Forward leg only, as before. The row refreshes when done.
    const handleRecal = (id, merchantId) => {
        setIsCalculating(true);
        recalculatePricing({
            orderId: id,
            merchantId,
            includeRTO: false
        })
            .then(() => {
                enqueueSnackbar("Done", { variant: 'success' });
                setReload((r) => r + 1);
            })
            .catch((err) => enqueueSnackbar(err.message, { variant: err.variant || 'error' }))
            .finally(() => setIsCalculating(false));
    };

    function OrderStatusWiseFilter() {
        const options = [...deliveryFailed, ...rto, ...transit, ...delivered, ...placed]
            .map((op) => ({ label: _.startCase(op), value: op }));
        return (
            <RHFSelectField name="status" width="100%" label="Order Status" placeholder="Select Order Status" displayEmpty options={_.orderBy(options, 'value')} />
        );
    }

    const ZoneList = ["LOCAL", "REGIONAL", "METROS", "ROI", "SPECIAL"]
        .map((val) => ({ label: _.startCase(val), value: val }));

    const handleClickCancel = (id) => { setCancell(true); setId(id); };
    const handleClose = () => { setCancell(false); setQc(false); };
    const onSubmit = (data) => {
        // Validation: if a Courier (parent) is picked, at least one Service
        // Type MUST also be picked. Show an error toast and don't apply
        // the filter — Service Type is the only mandatory pair-half here.
        if (data.parent && (!Array.isArray(data.serviceType) || data.serviceType.length === 0)) {
            enqueueSnackbar('Please select at least one Service Type for the chosen Courier.', { variant: 'error' });
            return;
        }
        setFiltersChange(filterChange + 1);
        setFilters(data);
    };
    const handleOpen = (row) => { setQc(true); setId(row); };

    const columns = [
        {
            field: 'merchant',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.2,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'orderDetails',
            headerName: 'Order Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                        <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>{row.shippingLabelId}</Box>
                    )}
                    {row.awbRegisteredDate && (
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.awbRegisteredDate)}</Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'statusDetails',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 155, flex: 1,
            renderCell: ({ row }) => <StatusChip status={row.orderStatus} />,
        },
        {
            field: 'zone',
            headerName: 'Zone', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => <ZoneBadge zone={row.merchant_pricing?.zone} />,
        },
        {
            field: 'courier',
            headerName: 'Courier & AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 190, flex: 1.4,
            renderCell: ({ row }) => {
                const courierParent = row.courierDetail?.parent?.includes('Blended')
                    ? row.actualCourierProviderName
                    : row.courierDetail?.parent;
                return (
                    <CellStack>
                        {courierParent
                            ? <CellLine bold>{courierParent}</CellLine>
                            : <CellLine muted>Unknown courier</CellLine>
                        }
                        {row.awb_number
                            ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <AwbLink awb={row.awb_number} id={row.id} />
                                    <CopyIcon text={row.awb_number} />
                                </Box>
                            )
                            : <Box sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Box>
                        }
                        {row.courierDetail?.prozoId && (
                            <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'pickup',
            headerName: 'Pickup', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => {
                const city = row.pickup_details?.from_city;
                const pincode = row.pickup_details?.from_pincode;
                if (!city && !pincode) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                const masked = shouldMaskDataReverse(report, row, piiAccessAllow);
                return (
                    <CellStack>
                        <CellLine bold>{masked ? maskAddress(city) : city}</CellLine>
                        {pincode && <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{pincode}</Box>}
                    </CellStack>
                );
            },
        },
        {
            field: 'delivery',
            headerName: 'Delivery', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.1,
            renderCell: ({ row }) => {
                const cityState = row.delivery_details?.to_city && row.delivery_details?.to_state
                    ? `${row.delivery_details.to_city}, ${row.delivery_details.to_state}`
                    : null;
                return (
                    <CellStack>
                        {cityState && <CellLine bold>{cityState}</CellLine>}
                        {row.delivery_details?.to_pincode && (
                            <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.delivery_details.to_pincode}</Box>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'shipment',
            headerName: 'Package', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.1,
            renderCell: ({ row }) => {
                const s = row.shipment_detail?.[0];
                if (!s) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                const dead = (s.item_weight / 1000).toFixed(2);
                const vol = ((s.item_length * s.item_breadth * s.item_height) / 5000).toFixed(2);
                const dim = `${s.item_length}×${s.item_breadth}×${s.item_height} cm`;
                return (
                    <CellStack>
                        <Box sx={{ fontSize: '12.5px', color: 'text.primary' }}><DataLabel>Dead</DataLabel>{dead} kg</Box>
                        <Box sx={{ fontSize: '12px', color: 'text.secondary' }}><DataLabel>Vol</DataLabel>{vol} kg</Box>
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{dim}</Box>
                    </CellStack>
                );
            },
        },
        {
            field: 'paymentDetails',
            headerName: 'Payment', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        {formatCurrency(row.invoice_value)}
                    </Box>
                    <PaymentBadge mode={row.payment_mode} />
                </CellStack>
            ),
        },
        {
            field: 'qcDetails',
            headerName: 'QC', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 80,
            renderCell: ({ row }) => {
                if (!row.qcDetails) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return (
                    <Box
                        component="button"
                        onClick={() => handleOpen(row)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                            borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            bgcolor: 'transparent', color: '#2563eb', border: '1px solid #bfdbfe',
                            transition: 'all 0.15s', '&:hover': { bgcolor: '#eff6ff', borderColor: '#93c5fd' },
                        }}
                    >
                        View
                    </Box>
                );
            },
        },
        {
            field: 'action1',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        {
                            key: 'view',
                            label: 'View details',
                            icon: <VisibilityIcon fontSize="small" />,
                            href: `/order-detail/${row.awb_number}?id=${row.id}`,
                            target: '_blank',
                        },
                        {
                            key: 'cancel',
                            label: 'Cancel order',
                            icon: <CancelIcon fontSize="small" />,
                            onClick: () => handleClickCancel(row),
                            color: 'error',
                            hidden: row.lifeCycleComplete === true,
                            dividerBefore: true,
                        },
                        {
                            key: 'resync',
                            label: 'Resync order',
                            icon: <SyncIcon fontSize="small" />,
                            onClick: () => setResyncModal({ open: true, awb: row.awb_number, reference: row.reference }),
                            dividerBefore: true,
                        },
                        {
                            key: 'recalculate',
                            label: 'Recalculate order value',
                            icon: <FunctionsIcon fontSize="small" />,
                            onClick: () => handleRecal(row.id, row.merchantDetail?.key),
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>Reverse Shipments</PageTitle>
            <MUIModal
                open={Cancell}
                handleClose={handleClose}
                children={<CancelOrder id={id} setReload={setReload} reload={reload} setCancell={setCancell} refetch={refetch} />}
                title={"Cancel Order"}
                hideClose={true}
                maxWidth={'md'}
            />
            {(isFetching || isLoading || isCalculating) && <Loader />}

            {/* ── Filter bar ────────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            {showParentFilter && (
                                <FilterField>
                                    <FieldLabel>Courier</FieldLabel>
                                    <RHFSearchableSelectField
                                        name="parent"
                                        label=""
                                        placeholder={parentOptions.length > 0 ? 'Select Courier' : 'No couriers available'}
                                        fullWidth
                                        options={parentOptions}
                                    />
                                </FilterField>
                            )}

                            {showServiceTypeFilter && (
                                <FilterField>
                                    <FieldLabel>
                                        Service Type <span style={{ color: '#dc2626' }}>*</span>
                                    </FieldLabel>
                                    <RHFMultipleSelectField
                                        name="serviceType"
                                        label=""
                                        placeholder={serviceTypeOptions.length > 0 ? 'Select Service Type' : 'No service type configured'}
                                        width="100%"
                                        size="small"
                                        outputFormat="array"
                                        search
                                        options={serviceTypeOptions}
                                    />
                                </FilterField>
                            )}

                            {showAccountCodeFilter && (
                                <FilterField>
                                    <FieldLabel>Account Code</FieldLabel>
                                    <RHFMultipleSelectField
                                        name="accountCode"
                                        label=""
                                        placeholder={accountCodeOptions.length > 0 ? 'All matching (optional)' : 'No account codes'}
                                        width="100%"
                                        size="small"
                                        outputFormat="array"
                                        search
                                        options={accountCodeOptions}
                                    />
                                </FilterField>
                            )}

                            <FilterField>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <FilterField>
                                <FieldLabel>Order Status</FieldLabel>
                                <OrderStatusWiseFilter />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Pickup Pincode</FieldLabel>
                                <RHFTextField name="pickupPincode" label="" placeholder="Pickup Pincode" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Zone</FieldLabel>
                                <RHFSelectField name="zone" label="" placeholder="Select Zone" options={_.orderBy(ZoneList, 'zone')} width="100%" size="small" displayEmpty />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Button type="submit" variant="contained" size="small"
                                    sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Button>
                                <Button type="button" variant="contained" size="small"
                                    sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                                    onClick={() => {
                                        // Reset restores the 90-day default rather than clearing the dates, so it
                                        // never reopens the unbounded query.
                                        reset(defaultFilterValues());
                                        setFilters(defaultFilterValues());
                                        setReload(() => reload + 1);
                                    }}>
                                    Reset
                                </Button>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={(data?.data && data?.data.length > 0) ? data?.data : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    getRowId={(row) => row.id}
                />
            </Box>

            <MUIModal open={qc} handleClose={handleClose} children={<QcDetails data={id} />} title={"Qc Link"} maxWidth="md" />
            <ResyncActionModal
                open={resyncModal.open}
                awb={resyncModal.awb}
                reference={resyncModal.reference}
                onClose={() => setResyncModal({ open: false, awb: null, reference: null })}
                onDone={() => setReload(reload + 1)}
            />
        </>
    );
};

export default AllRVPOrders;
